import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

const BASE_PROFILES_PATH = `${process.env.PUBLIC_URL || ''}/client_profiles_full.json`;

const METADATA_LABELS = {
  num_daily: 'Cigarettes per day',
  first_cig: 'First cigarette after waking up',
  pre_convo_quit_attempt_made: 'Attempted to quit (week before)',
  confi_pre: 'Confidence (pre)',
  impor_pre: 'Importance (pre)',
  readi_pre: 'Readiness (pre)',
};

const toTitleCase = (value = '') =>
  value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const toArray = (value) => {
  if (!value && value !== 0) {
    return [];
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === 'string' ? item.trim() : item))
      .filter((item) => item !== null && item !== undefined && item !== '');
  }
  if (typeof value === 'string') {
    return value.trim() ? [value.trim()] : [];
  }
  return [value];
};

const normalizeStatement = (item) => {
  if (!item && item !== 0) {
    return null;
  }
  if (typeof item === 'string') {
    const trimmed = item.trim();
    return trimmed ? { text: trimmed } : null;
  }
  if (typeof item === 'object') {
    const textValue =
      item.text ||
      item.statement ||
      item.description ||
      item.note ||
      item.value ||
      item.content ||
      '';
    const text = typeof textValue === 'string' ? textValue.trim() : textValue;
    if (!text) {
      return null;
    }
    const category =
      item.category_label ||
      item.category ||
      item.code ||
      item.label ||
      item.type ||
      item.tag ||
      item.polarity ||
      item.sentiment ||
      '';
    return {
      text,
      category,
    };
  }
  return { text: String(item) };
};

const normalizeStatements = (primary, fallback) => {
  let statements = toArray(primary);
  if (!statements.length) {
    statements = toArray(fallback);
  }
  return statements
    .map(normalizeStatement)
    .filter((entry) => entry && entry.text);
};

const formatMetadataEntries = (metadataObj = {}) => {
  return Object.entries(metadataObj).map(([key, value]) => ({
    key,
    label: METADATA_LABELS[key] || toTitleCase(key),
    value: value === null || value === undefined || value === '' ? '—' : value,
  }));
};

const buildProfileDetails = (profile) => {
  if (!profile) {
    return {
      personaList: [],
      motivationList: [],
      barrierList: [],
      nextSteps: [],
      metadataEntries: [],
      transcriptLines: [],
    };
  }

  let personaList = toArray(profile.persona);
  if (!personaList.length) {
    personaList = toArray(profile.profile?.Persona);
  }

  const motivationList = normalizeStatements(
    profile.motivations_for_change,
    profile.profile?.Motivation
  );

  const barrierList = normalizeStatements(
    profile.barriers_against_change,
    profile.profile?.Beliefs
  );

  let nextSteps = toArray(profile.potential_next_steps);
  if (!nextSteps.length) {
    nextSteps = toArray(profile.profile?.Acceptable_Plan);
  }

  const metadataSource =
    profile.metadata ||
    profile.profile?.metadata ||
    profile.profile?.Metadata ||
    {};

  const metadataEntries = formatMetadataEntries(metadataSource);

  const transcriptLines = Array.isArray(profile.transcript)
    ? profile.transcript
    : [];

  return {
    personaList,
    motivationList,
    barrierList,
    nextSteps,
    metadataEntries,
    transcriptLines,
  };
};

export const getMetaKeyForPersona = (text) => {
  if (!text) return null;
  const lowerText = text.toLowerCase();
  if (lowerText.includes('cigarettes per day') || (lowerText.includes('smokes') && lowerText.includes('per day'))) {
    return 'num_daily';
  }
  if (lowerText.includes('first cigarette') && lowerText.includes('after waking')) {
    return 'first_cig';
  }
  if (lowerText.includes('attempt to quit') || lowerText.includes('quit attempt')) {
    return 'pre_convo_quit_attempt_made';
  }
  if (lowerText.includes('confident') && lowerText.includes('succeed')) {
    return 'confi_pre';
  }
  if (lowerText.includes('important') && lowerText.includes('stop smoking')) {
    return 'impor_pre';
  }
  if (lowerText.includes('ready') && (lowerText.includes('making a change') || lowerText.includes('start'))) {
    return 'readi_pre';
  }
  return null;
};

function useClientProfiles(activeOverlay) {
  const [clientProfilesExtended, setClientProfilesExtended] = useState([]);
  const [clientProfilesLoading, setClientProfilesLoading] = useState(false);
  const [clientProfilesError, setClientProfilesError] = useState(null);
  const [activeClientProfile, setActiveClientProfile] = useState(null);
  const [activeClientDisplayIndex, setActiveClientDisplayIndex] = useState(0);
  const [clientProfileVisible, setClientProfileVisible] = useState(false);
  const [highlightedMetaKey, setHighlightedMetaKey] = useState(null);

  const activeClientProfileIdRef = useRef(null);

  const normalizedProfileDetails = useMemo(
    () => buildProfileDetails(activeClientProfile),
    [activeClientProfile]
  );

  const {
    personaList,
    motivationList,
    barrierList,
    nextSteps,
    metadataEntries,
    transcriptLines,
  } = normalizedProfileDetails;

  useEffect(() => {
    activeClientProfileIdRef.current = activeClientProfile ? activeClientProfile.profile_id : null;
  }, [activeClientProfile]);

  const handleClientProfileTabSelect = useCallback((profile, index) => {
    setActiveClientProfile(profile);
    setActiveClientDisplayIndex(index);
    setClientProfileVisible(false);
  }, []);

  const handleClientProfileReveal = useCallback(() => {
    setClientProfileVisible(true);
  }, []);

  const resetClientProfileVisibility = useCallback(() => {
    setClientProfileVisible(false);
  }, []);

  const fetchClientProfiles = useCallback(async (preserveSelection = true) => {
    setClientProfilesLoading(true);
    try {
      const response = await fetch(BASE_PROFILES_PATH, { cache: 'default' });
      if (!response.ok) {
        throw new Error('Failed to load client profiles');
      }
      const data = await response.json();
      const profiles = data?.profiles || [];
      setClientProfilesExtended(profiles);

      if (profiles.length > 0) {
        const previousId = preserveSelection ? activeClientProfileIdRef.current : null;
        let nextActiveIndex = 0;
        if (previousId !== null) {
          const foundIndex = profiles.findIndex((profile) => profile.profile_id === previousId);
          if (foundIndex !== -1) {
            nextActiveIndex = foundIndex;
          }
        }
        setActiveClientProfile(profiles[nextActiveIndex]);
        setActiveClientDisplayIndex(nextActiveIndex);
        setClientProfileVisible(false);
      }
      setClientProfilesError(null);
    } catch (err) {
      console.error('Error fetching client profiles:', err);
      setClientProfilesError('Unable to load detailed client data right now.');
    } finally {
      setClientProfilesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClientProfiles();
  }, [fetchClientProfiles]);

  useEffect(() => {
    if (activeOverlay === 'client' && clientProfilesExtended.length === 0 && !clientProfilesLoading) {
      fetchClientProfiles();
    }
  }, [activeOverlay, clientProfilesExtended.length, clientProfilesLoading, fetchClientProfiles]);

  useEffect(() => {
    if (
      activeOverlay === 'client' &&
      clientProfilesExtended.length > 0 &&
      !clientProfilesLoading &&
      clientProfilesExtended.every(
        (profile) => !Array.isArray(profile.transcript) || profile.transcript.length === 0
      )
    ) {
      fetchClientProfiles();
    }
  }, [activeOverlay, clientProfilesExtended, clientProfilesLoading, fetchClientProfiles]);

  useEffect(() => {
    if (activeOverlay === 'client' && clientProfilesExtended.length > 0 && !activeClientProfile) {
      setActiveClientProfile(clientProfilesExtended[0]);
      setActiveClientDisplayIndex(0);
      setClientProfileVisible(false);
    }
  }, [activeOverlay, clientProfilesExtended, activeClientProfile]);

  return {
    clientProfilesExtended,
    clientProfilesLoading,
    clientProfilesError,
    activeClientProfile,
    activeClientDisplayIndex,
    clientProfileVisible,
    highlightedMetaKey,
    setHighlightedMetaKey,
    personaList,
    motivationList,
    barrierList,
    nextSteps,
    metadataEntries,
    transcriptLines,
    handleClientProfileTabSelect,
    handleClientProfileReveal,
    resetClientProfileVisibility,
  };
}

export default useClientProfiles;
